import styled, { keyframes, css } from 'styled-components';

/* ═══════════════════════════════════════════
   V2 Design System — Shared Styled Components
   ═══════════════════════════════════════════ */

// ── Animations ──
export const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-8px); }
`;

export const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.7; }
`;

// ── Layout ──
export const Section = styled.section`
  width: 100%;
  padding: ${({ $py }) => $py || '64px'} 0;

  @media (max-width: 768px) {
    padding: ${({ $pyMobile }) => $pyMobile || '40px'} 0;
  }
`;

export const Inner = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

export const SectionHeader = styled.div`
  text-align: ${({ $align }) => $align || 'center'};
  margin-bottom: 40px;
  animation: ${fadeInUp} 0.6s ease both;

  @media (max-width: 768px) {
    margin-bottom: 28px;
  }
`;

export const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.bold};
  font-size: 2rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.neutral[900]};
  margin: 0 0 8px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.neutral[500]};
  margin: 0;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 9999px;
  letter-spacing: 0.5px;
  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'primary':
        return css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[700]};
        `;
      case 'success':
        return css`
          background: #ecfdf5;
          color: #059669;
        `;
      case 'warning':
        return css`
          background: #fffbeb;
          color: #d97706;
        `;
      default:
        return css`
          background: ${theme.colors.neutral[100]};
          color: ${theme.colors.neutral[600]};
        `;
    }
  }}
`;

// ── Cards ──
export const Card = styled.div`
  background: ${({ theme }) => theme.colors.neutral[0]};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  box-shadow: ${({ theme }) => theme.elevation.sm};
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    box-shadow: ${({ theme }) => theme.elevation.lg};
    transform: translateY(-4px);
    border-color: ${({ theme }) => theme.colors.primary[200]};
  }
`;

// ── Grid ──
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 3}, 1fr);
  gap: ${({ $gap }) => $gap || '24px'};

  @media (max-width: 1024px) {
    grid-template-columns: repeat(${({ $colsTablet }) => $colsTablet || 2}, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(${({ $colsMobile }) => $colsMobile || 1}, 1fr);
    gap: ${({ $gapMobile }) => $gapMobile || '16px'};
  }
`;

// ── Divider ──
export const Divider = styled.div`
  width: 48px;
  height: 4px;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary[400]}, ${({ theme }) => theme.colors.primary[600]});
  border-radius: 2px;
  margin: ${({ $center }) => $center ? '0 auto 16px' : '0 0 16px'};
`;
